/**
 * SettingsTab - Store settings management component
 * Extracted from AdminPage.jsx
 */
import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '../../../utils/apiClient';

export default function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/settings')
      .then(res => {
        setSettings(res.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved successfully!');
    } catch (err) {
      toast.error('Failed to save settings');
    }
    setSaving(false);
  };

  const updateSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
  };

  if (loading) {
    return <div className="animate-pulse bg-muted h-96 rounded-2xl" />;
  }

  return (
    <div data-testid="settings-tab">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-heading text-3xl text-foreground">Store Settings</h1>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="rounded-full bg-primary hover:bg-primary/90"
          data-testid="save-settings-btn"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Wait Time Settings */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-4 flex items-center gap-2 text-foreground">
            <Clock className="w-5 h-5 text-primary" />
            Wait Time Settings
          </h2>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">Wait Time Mode</Label>
              <Select 
                value={settings?.wait_time?.mode || 'fixed'} 
                onValueChange={(value) => updateSetting('wait_time', 'mode', value)}
              >
                <SelectTrigger data-testid="wait-time-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Time</SelectItem>
                  <SelectItem value="queue_based">Queue-Based (Dynamic)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Fixed Wait Time (minutes)</Label>
              <Input
                type="number"
                value={settings?.wait_time?.fixed_minutes || 10}
                onChange={(e) => updateSetting('wait_time', 'fixed_minutes', parseInt(e.target.value))}
                data-testid="fixed-minutes-input"
              />
              <p className="text-xs text-muted-foreground">
                Used when mode is "Fixed Time"
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Per-Item Time (minutes)</Label>
              <Input
                type="number"
                value={settings?.wait_time?.per_item_minutes || 3}
                onChange={(e) => updateSetting('wait_time', 'per_item_minutes', parseInt(e.target.value))}
                data-testid="per-item-minutes-input"
              />
              <p className="text-xs text-muted-foreground">
                Used in queue-based mode
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-foreground">Maximum Wait Time (minutes)</Label>
              <Input
                type="number"
                value={settings?.wait_time?.max_minutes || 45}
                onChange={(e) => updateSetting('wait_time', 'max_minutes', parseInt(e.target.value))}
                data-testid="max-minutes-input"
              />
            </div>
          </div>
        </div>

        {/* Payment Settings */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-4 text-foreground">Payment Methods</h2>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-3 bg-muted rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.payments?.counter_payment_enabled !== false}
                onChange={(e) => updateSetting('payments', 'counter_payment_enabled', e.target.checked)}
                className="rounded"
                data-testid="counter-payment-toggle"
              />
              <div>
                <p className="font-medium text-foreground">Pay at Counter</p>
                <p className="text-xs text-muted-foreground">Allow cash/card payment at pickup</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 bg-muted rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.payments?.mpaisa_enabled || false}
                onChange={(e) => updateSetting('payments', 'mpaisa_enabled', e.target.checked)}
                className="rounded"
                data-testid="mpaisa-toggle"
              />
              <div>
                <p className="font-medium text-foreground">M-PAiSA</p>
                <p className="text-xs text-muted-foreground">Mobile money transfer</p>
              </div>
            </label>

            {settings?.payments?.mpaisa_enabled && (
              <div className="ml-6 space-y-3 border-l-2 border-primary/20 pl-4">
                <div className="space-y-2">
                  <Label className="text-foreground">M-PAiSA Number</Label>
                  <Input
                    value={settings?.payments?.mpaisa_number || ''}
                    onChange={(e) => updateSetting('payments', 'mpaisa_number', e.target.value)}
                    placeholder="+679 XXX XXXX"
                    data-testid="mpaisa-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Account Name</Label>
                  <Input
                    value={settings?.payments?.mpaisa_name || ''}
                    onChange={(e) => updateSetting('payments', 'mpaisa_name', e.target.value)}
                    placeholder="Bulaccino Cafe"
                    data-testid="mpaisa-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground">Instructions</Label>
                  <Textarea
                    value={settings?.payments?.mpaisa_instructions || ''}
                    onChange={(e) => updateSetting('payments', 'mpaisa_instructions', e.target.value)}
                    rows={2}
                    data-testid="mpaisa-instructions-input"
                  />
                </div>
              </div>
            )}

            <label className="flex items-center gap-3 p-3 bg-muted rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.payments?.stripe_enabled || false}
                onChange={(e) => updateSetting('payments', 'stripe_enabled', e.target.checked)}
                className="rounded"
                data-testid="stripe-toggle"
              />
              <div>
                <p className="font-medium text-foreground">Stripe (Credit/Debit Card)</p>
                <p className="text-xs text-muted-foreground">Requires API keys in .env</p>
              </div>
            </label>

            <label className="flex items-center gap-3 p-3 bg-muted rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.payments?.paypal_enabled || false}
                onChange={(e) => updateSetting('payments', 'paypal_enabled', e.target.checked)}
                className="rounded"
                data-testid="paypal-toggle"
              />
              <div>
                <p className="font-medium text-foreground">PayPal</p>
                <p className="text-xs text-muted-foreground">Requires API keys in .env</p>
              </div>
            </label>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-4 text-foreground">SMS Notifications</h2>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-3 bg-muted rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.notifications?.sms_enabled || false}
                onChange={(e) => updateSetting('notifications', 'sms_enabled', e.target.checked)}
                className="rounded"
                data-testid="sms-enabled-toggle"
              />
              <div>
                <p className="font-medium text-foreground">Enable SMS Notifications</p>
                <p className="text-xs text-muted-foreground">Requires Twilio API keys in .env</p>
              </div>
            </label>

            {settings?.notifications?.sms_enabled && (
              <div className="ml-6 space-y-3 border-l-2 border-primary/20 pl-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings?.notifications?.sms_on_confirmed !== false}
                    onChange={(e) => updateSetting('notifications', 'sms_on_confirmed', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-foreground">Send when order is confirmed</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings?.notifications?.sms_on_ready !== false}
                    onChange={(e) => updateSetting('notifications', 'sms_on_ready', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm text-foreground">Send when order is ready</span>
                </label>
              </div>
            )}
          </div>
        </div>

        {/* Display Settings */}
        <div className="bg-card rounded-2xl p-6 shadow-sm border border-border">
          <h2 className="font-heading text-xl mb-4 text-foreground">Display Settings</h2>
          
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-3 bg-muted rounded-xl cursor-pointer">
              <input
                type="checkbox"
                checked={settings?.dark_mode_enabled !== false}
                onChange={(e) => setSettings(prev => ({ ...prev, dark_mode_enabled: e.target.checked }))}
                className="rounded"
                data-testid="dark-mode-toggle"
              />
              <div>
                <p className="font-medium text-foreground">Allow Dark Mode</p>
                <p className="text-xs text-muted-foreground">Let users toggle dark mode</p>
              </div>
            </label>

            <div className="space-y-2">
              <Label className="text-foreground">Custom QR Code URL (optional)</Label>
              <Input
                value={settings?.qr_code_url || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, qr_code_url: e.target.value }))}
                placeholder="https://bulaccinocafe.com/order"
                data-testid="qr-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Leave empty to use default ordering page
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
